import {
  Container,
  Form,
  InputField,
  Page,
  PageTitle,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import { Button } from '@windmill/react-ui';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { readApplicationForSubmission } from '../../common/api/application';
import { AppStatus, AppType } from '../../common/enums';
import { DeveloperOrganizationContextProps } from '../../common/providers/types';
import { setApplicationSubmission } from '../../common/reducers/applicationSubmissionSlice';
import { AppStatusBadge } from './AppStatusBadge';

interface AppSummaryForm {
  appId: string;
  listingId: string;
  urlPath: string;
  webAppUrl: string;
  landingPageUrl: string;
  listingName: string;
  appType: AppType;
  shortDescription: string;
  isPublic: boolean;
  version: string;
  consoleCompatible: boolean;
}

export const ApplicationSummary: React.FunctionComponent = () => {
  const { applicationId } = useParams();
  const { currentOrg, currentTenant } =
    useOrganization() as DeveloperOrganizationContextProps;
  const { currentTeam } = useTeam();
  const [application, setApplication] = React.useState<ApplicationSubmission>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<AppSummaryForm>({
    mode: 'onChange',
    defaultValues: useMemo(() => {
      return application;
    }, [application]),
  });

  React.useEffect(() => {
    reset(application);
  }, [application]);

  React.useEffect(() => {
    if (
      applicationId &&
      currentOrg &&
      currentTenant &&
      currentTeam &&
      !application
    ) {
      const getApplication = async () => {
        const app = await readApplicationForSubmission(
          currentOrg.id,
          currentTeam.id,
          applicationId
        );

        if (app) {
          setApplication(app);
        }
      };
      getApplication();
    }
  }, [applicationId, currentOrg, currentTeam, currentTenant]);

  if (
    !application ||
    !currentOrg ||
    !currentTenant ||
    currentTenant.orgId != currentOrg.id
  ) {
    return null;
  }

  const onSubmit = () => {
    dispatch(setApplicationSubmission(application));

    navigate(`/application/${applicationId}/publish`, {
      replace: false,
    });
  };

  return (
    <Page className="flex-col items-center">
      <Container
        className="mb-os1-spacing flex flex-col justify-start gap-6"
        size="medium"
      >
        <Form
          onSubmit={handleSubmit(onSubmit)}
          title={
            <div className="flex flex-row items-center gap-x-3">
              <PageTitle>Summary for {application.listingName}</PageTitle>
              <AppStatusBadge status={application.status} />
            </div>
          }
        >
          <div className="w-full flex flex-row items-center gap-x-3">
            <InputField
              disabled={true}
              errors={errors}
              inputKey="appId"
              placeholder={application.appId}
              register={register}
              required={false}
              testId="appId-input"
              title="App ID"
            />
            <InputField
              disabled={true}
              errors={errors}
              inputKey="listingId"
              placeholder={application.listingId}
              register={register}
              required={false}
              testId="listingId-input"
              title="Listing ID"
            />
          </div>
          <div className="flex flex-row items-center gap-x-3">
            <InputField
              disabled={true}
              errors={errors}
              inputKey="listingName"
              placeholder={application.listingName}
              register={register}
              required={false}
              testId="listingName-input"
              title="App Name"
            />
            <InputField
              disabled={true}
              errors={errors}
              inputKey="appType"
              placeholder={application.appType}
              register={register}
              required={false}
              testId="appType-input"
              title="App Type"
            />
          </div>
          <div className="flex flex-row items-center gap-x-3">
            <InputField
              disabled={true}
              errors={errors}
              inputKey="version"
              placeholder={application.version}
              register={register}
              required={false}
              testId="version-input"
              title="Version"
            />
            <InputField
              disabled={true}
              errors={errors}
              inputKey="urlPath"
              placeholder={application.urlPath}
              register={register}
              required={false}
              testId="urlPath-input"
              title="App Path URL"
            />
          </div>
          <div className="w-full flex flex-col items-left gap-x-3">
            <InputField
              className="w-full"
              data-testid="shortDescription-input"
              disabled={true}
              errors={errors}
              inputKey="shortDescription"
              placeholder={application.shortDescription}
              register={register}
              required={false}
              testId="shortDescription-input"
              title="Description (50 - 512 characters)"
            />
          </div>
          <div className="w-full flex flex-col items-left gap-x-3">
            {application.landingPageUrl && (
              <InputField
                className="w-full"
                disabled={true}
                errors={errors}
                inputKey="landingPageUrl"
                placeholder={application.landingPageUrl}
                register={register}
                required={false}
                testId="landingPageUrl-input"
                title="Landing Page URL"
              />
            )}
          </div>

          <div className="flex flex-row items-center gap-x-3">
            {application.status == AppStatus.DEVELOPMENT && (
              <Button className="h-10" size="large" type="submit">
                Submit App for Review
              </Button>
            )}
            {application.status != AppStatus.DEVELOPMENT && (
              <Button
                className="h-10"
                onClick={() => navigate('/contact-us')}
                size="large"
                type="submit"
              >
                Contact Us
              </Button>
            )}
          </div>
        </Form>
      </Container>
    </Page>
  );
};
